import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { WebPageSchema, BreadcrumbSchema } from '@/components/feature/SchemaOrg';
import useSEO from '@/hooks/useSEO';
import { articlesByCategory, categoryMeta } from '@/mocks/category';
import { categories } from '@/mocks/home';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'entertainment');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeCategory]);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
    }
  }, [slug]);

  const meta = categoryMeta[activeCategory] || categoryMeta.entertainment;
  const articles = articlesByCategory[activeCategory] || articlesByCategory.entertainment;

  useSEO({
    title: `${meta.name} - 亚洲在线${meta.name}平台`,
    description: meta.description,
    keywords: meta.keywords,
    canonicalUrl: `https://taokep.cn/category/${activeCategory}`,
  });

  const breadcrumbItems = [
    { name: '首页', url: 'https://taokep.cn/' },
    { name: meta.name, url: `https://taokep.cn/category/${activeCategory}` },
  ];

  return (
    <>
      <WebPageSchema
        url={`https://taokep.cn/category/${activeCategory}`}
        name={`${meta.name} - 亚洲在线`}
        description={meta.description}
        breadcrumbs={breadcrumbItems}
      />
      <BreadcrumbSchema items={breadcrumbItems} />

      <Navbar />
      <main>
        <section className="relative w-full h-[320px] md:h-[400px] flex items-center justify-center overflow-hidden">
          <img
            src={meta.image}
            alt={meta.name}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/65"></div>
          <div className="relative z-10 text-center px-4">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-50/15 backdrop-blur-sm border border-background-50/20 mb-4">
              <i className={`${meta.icon} text-2xl text-background-50`}></i>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-3">
              {meta.name}
            </h1>
            <p className="text-sm md:text-base text-background-100/70 max-w-lg mx-auto">
              {meta.description}
            </p>
          </div>
        </section>

        <nav className="bg-background-50 border-b border-background-200/70" aria-label="Breadcrumb">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-3">
            <ol className="flex items-center gap-2 text-xs text-foreground-500 flex-wrap">
              <li><Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link></li>
              <li><i className="ri-arrow-right-s-line"></i></li>
              <li className="text-foreground-900 font-medium">{meta.name}</li>
            </ol>
          </div>
        </nav>

        <section className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-3">
            <div className="flex items-center gap-2 overflow-x-auto flex-wrap pb-1">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveCategory(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                    activeCategory === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5 text-sm`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group overflow-hidden rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-2 text-xs text-foreground-500 flex-wrap">
                        <span className="px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 font-medium">{article.category}</span>
                        <span>{article.date}</span>
                        <span>{article.readTime}</span>
                      </div>
                      <h3 className="font-heading text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-3 text-xs text-foreground-500">
                        <div className="w-5 h-5 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-medium text-xs">
                          {article.author.charAt(0)}
                        </div>
                        <span>{article.author}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无文章</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}